import _ from 'lodash'
import $ from 'jquery'
import * as d3 from 'd3-selection'
import EventEmitter from 'events'
import spotlight from 'scoreboard/components/spotlight'

/**
 * Base class for a stack in the table.  Stack classes are used by the table
 * code to construct the contents, layout, and behavior of a stack within the
 * table.
 */
class TableStack extends EventEmitter {

	constructor(index, columnsAttributes, opts = {}) {
		super()
		this.index = index
		this.options = opts
		this._rows = []
		this._formatters = []
		this._bodyDomNodes = []
		this._selected = false
		this.stack = this.options.stack
		this.dataExtractor = this.options.dataExtractor
	}

	setRow(stacks, index) {
		_.each(stacks, cell => cell.rowIndex(index))
		this._rows[index] = stacks
	}

	setCellFormatters(formatters) {
		this._formatters = [].concat(formatters)
	}

	rows(at = null) {
		return _.isNil(at) ? this._rows : this._rows[at]
	}

	cellFormatters() {
		return this._formatters
	}

	bindStackBodyDomNode(td) {
		this._bodyDomNodes.push(td)
		d3.select(td).on('click', () => {
			this.emit('selectCell', this, td)
		})
	}

	select(state = null, targetCell = null, targetDomNode, opts = {}) {
		if (!_.isNil(state)) {
			this._selected = !!state
		} else {
			this._selected = !this._selected
		}
		if (!opts.silent) {
			this.emit('select', this, this._selected)
		}
		return this._selected
	}

	headerDomNodes() {
		return _.flatMap(this.rows(), row => _.flatMap(row, stack => stack.domNodes()))
	}

	bodyDomNodes() {
		return this._bodyDomNodes
	}

	resetBodyDomNodes() {
		this._bodyDomNodes = []
	}

	domNodes() {
		return [...this.headerDomNodes(), ...this.bodyDomNodes()]
	}

	defaultCellFormatter(dataExtractor, headerCell) {
		return (datum, dataRowIndex) => {
			let cellData = {
				tableStack: this,
				headerCell
			}
			return _.defaults(cellData, dataExtractor.toCellContents(
				datum, headerCell, dataRowIndex
			))
		}
	}

	/**
	 * Called on the start of the drag, performs any initialization required
	 * when a cell from this stack start a drag
	 */
	drag(dragCell, enable) {
		throw new Error("Subclass must implement")
	}

	/**
	 * Called when a entering/leaving a cell where a drop is accepted
	 *
	 * @param {StackCell} args.dragCell the cell being dragged
	 * @param {StackCell} args.dropCell the cell where the drop is being accepted
	 * @param {Object} args.zone contains positional information about the drag
	 * @param {Boolean} args.enter true if entering the drop zone, false if leaving
	 * @param {Object} args.adjacentZones the zones adjacent to the zone passed it
	 */
	accept(args) {
		throw new Error("Subclass must implement")
	}

	/**
	 * Returns true if this stack is allowing in the dragged cell to be
	 * dropped on the drop cell
	 *
	 * @param {StackCell} dragCell the cell being dragged
	 * @param {StackCell} dropCell the cell where the drop is being accepted
	 * @param {Object} zone contains positional information about the drag
	 */
	allowDrop(dragCell, dropCell, zone) {
		throw new Error("Subclass must implement")
	}

	/**
	 * Function to apply a spotlight affect on the stack
	 */
	applySpotlight() {
		if (_.get(this.options, 'stack.spotlightIndex', null) != null) {
			_.each(this._spotlightNodes(), spotlight)
			delete this.options.stack.spotlightIndex
		}
	}

	/**
	 * Returns the dom nodes to be spotlighted, default is the entire stack,
	 * subclasses should define this behavior since different stack types might
	 * exhibit different spotlighting effects
	 */
	_spotlightNodes() {
		return this.domNodes()
	}

	clearDropLines() {
		$(this.domNodes()).toggleClass(['droppable','left', 'right',
			'drop-line-horizontal','drop-line-vertical'],false)
	}
}

export default TableStack
TableStack.__filename__ = 'reports/common/table/table-stack.js'
